import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AgCharts } from "ag-charts-react";
import { type AgChartOptions, ModuleRegistry, CategoryAxisModule, LegendModule, NumberAxisModule, DonutSeriesModule, BarSeriesModule, LineSeriesModule } from "ag-charts-community";
ModuleRegistry.registerModules([ CategoryAxisModule, LegendModule, NumberAxisModule, DonutSeriesModule, BarSeriesModule, LineSeriesModule ]);

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";

interface State {
}

const defState = {
};

interface SearchParams {
  statMonth: string,
  pnLike: string,
  mngType: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  statMonth: dayjs().format("YYYYMM"),
  pnLike: "",
  mngType: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  monthInfos: any[],
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  monthInfos: [],
  infos: []
};

function MonthlyInoutStatus(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const chartData = useMemo(() => {
    return result.monthInfos.map((month: any) => {
      const key = "m" + month;
      const row: any = { month: kutil.date2string(`${month}01`, "yyyy-MM") };
      result.infos.forEach((info: any) => {
        row[info.inoutType] = info[key];
      });
      return row;
    });
  }, [result]);

  const chartOptions = useMemo<AgChartOptions>(() => ({
    data: chartData,
    series: [
      { type: "line", xKey: "month", yKey: "입고", yName: "입고", stroke: "#051766", marker: { shape: "circle", fill: "#fff", stroke: "#051766", size: 8 }, interpolation: { type: "smooth" } },
      { type: "line", xKey: "month", yKey: "출고", yName: "출고", stroke: "#57bbeb", marker: { fill: "#fff", stroke: "#57bbeb", size: 8 }, interpolation: { type: "smooth" } },
      { type: "line", xKey: "month", yKey: "Total", yName: "Total", stroke: "#28794e", marker: { fill: "#fff", stroke: "#28794e", size: 8 }, interpolation: { type: "smooth" } },
    ],
    axes: {
      x: {
        type: "category",
        position: "bottom",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", fontSize: 13 },
        line: { enabled: true },
        gridLine: { enabled: false },
      },
      y: {
        type: "number",
        position: "left",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", fontSize: 13 },
        line: { enabled: true },
        gridLine: { enabled: false },
      }
    },
    legend: {
      enabled: true,
      position: "top",
      item: {
        label: { fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", fontSize: 14 }
      }
    }
  }), [chartData]);

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/report/monthly_inout_status.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        monthInfos: data.statMonths,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/report/monthly_inout_status", searchParams);
  }

  async function resetParams() {
    router.navigateByPath("/report/monthly_inout_status", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/report/monthly_inout_status.download?mode=search", searchParams, {
        "fileName": "월별입출고현황_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents report-chart">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>월별 입출고 현황</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>조회월</p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: 180 }}
                slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                views={["year", "month"]}
                openTo="month"
                format="YYYY-MM"
                value={searchParams.statMonth ? dayjs(searchParams.statMonth, "YYYYMM") : null}
                onChange={(e) => {doSearch(e, {statMonth: (e != null ? dayjs(e).format("YYYYMM") : "")})}}
              />
            </LocalizationProvider>
          </div>
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>관리구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.mngType}
                onChange={(e) => {doSearch(e, {mngType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.mngType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 차트 */}
        <div className="chart-area">
          <AgCharts options={chartOptions} style={{ width: "92%", height: "95%" }} />
        </div>
        {/*// 차트 */}
        {/* 통계 정보 */}
        <div className="table-wrap">
          <table className="tbl-list">
            <colgroup>
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
              <col style={{ width: "7.14%" }} />
            </colgroup>
            <thead>
              <tr>
                <th scope="row">구분</th>
          {result.monthInfos.map((month: any) => {
            return (
                <th scope="col">{kutil.date2string(`${month}01`, "yyyy-MM")}</th>
            );
          })}
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
          {result.infos.map((info: any, idx: number) => {
            const { inoutType, total, ...hours } = info;
            return (
              <tr key={idx}>
                <th scope="row">{inoutType}</th>
                {Object.values(hours).map((v: any, i: number) => (
                  <td key={i}>{valueFormatter.number(v)}</td>
                ))}
                <td>{valueFormatter.number(total)}</td>
              </tr>
            );
          })}
            </tbody>
          </table>
        </div>
        {/*// 통계 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(MonthlyInoutStatus);
